import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLandmark,
  faUserGraduate,
  faUsers,
  faUserTie,
  faCoins,
  faTicket,
  faGift,
  faLanguage,
  faNoteSticky,
  faQuestionCircle,
  faChartLine,
  faCube,
} from "@fortawesome/free-solid-svg-icons";

/* ------------------ Dummy Data ------------------ */

const galleries = [
  {
    id: 1,
    name: "Ancient Bihar Civilization",
    description: {
      en: "Explore the roots of one of the world's oldest civilizations.",
      hi: "विश्व की प्राचीनतम सभ्यताओं में से एक की जड़ों को जानें।",
    },
    audio: "https://example.com/audio",
    video: "https://example.com/video",
  },
  {
    id: 2,
    name: "Buddhism & Nalanda",
    description: {
      en: "Bihar as the global knowledge capital.",
      hi: "वैश्विक ज्ञान केंद्र के रूप में बिहार।",
    },
    audio: "https://example.com/audio",
    video: "https://example.com/video",
  },
];

/* ------------------ UI Components ------------------ */

const SectionCard = ({ title, children }) => (
  <div className="bg-gray-900 rounded-2xl p-4 shadow-lg mb-4">
    <h2 className="text-lg font-semibold mb-3 text-yellow-400">{title}</h2>
    {children}
  </div>
);

/* ------------------ Stakeholder Sections ------------------ */

const VisitorSection = () => {
  const [lang, setLang] = useState("en");
  const [tokens, setTokens] = useState(12);

  return (
    <>
      <SectionCard title="Visitor Engagement">
        <div className="grid grid-cols-4 gap-3">
          <button className="bg-cyan-600 rounded-lg py-2">
            <FontAwesomeIcon icon={faCube} /> Digital Twin
          </button>            
          <button className="bg-blue-600 rounded-lg py-2">
            <FontAwesomeIcon icon={faNoteSticky} /> Notes
          </button>
          <button className="bg-green-600 rounded-lg py-2">
            <FontAwesomeIcon icon={faQuestionCircle} /> Quiz
          </button>
          <button className="bg-purple-600 rounded-lg py-2">
            🎁 Surprise Reward
          </button>
        </div>
      </SectionCard>
      <SectionCard title="Gallery Explorer">
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setLang("en")}
            className={`w-10 px-3 py-1 rounded ${
              lang === "en" ? "bg-yellow-600 text-black" : "bg-gray-700"
            }`}
          >
            EN
          </button>
          <button
            onClick={() => setLang("hi")}
            className={`w-10 px-3 py-1 rounded ${
              lang === "hi" ? "bg-yellow-600 text-black" : "bg-gray-700"
            }`}
          >
            हिंदी
          </button>
        </div>

        {galleries.map((g) => (
          <div key={g.id} className="mb-4 border-b border-gray-700 pb-3">
            <h3 className="font-semibold text-white">{g.name}</h3>
            <p className="text-sm text-white mt-1">
              {g.description[lang]}
            </p>
            <div className="flex gap-3 mt-2 text-sm">
              <a href={g.audio} className="text-blue-400">
                🎧 Audio
              </a>
              <a href={g.video} className="text-blue-400">
                🎥 Video
              </a>
            </div>
          </div>
        ))}
        <p className="text-sm text-gray-300 mt-3">
          🎟 Visitor Tokens: <span className="text-yellow-400">{tokens}</span>
        </p>
      </SectionCard>
    </>
  );
};

const StudentSection = () => (
  <>
    <SectionCard title="Learning & Development">
      <ul className="list-disc ml-5 [&>li]:text-white text-sm">
        <li>NEP-aligned Learning Path</li>
        <li>5-Why Interactive Thinking</li>
        <li>Digital Notes & Reflection</li>
        <li>Micro Quizzes</li>
      </ul>
    </SectionCard>

    <SectionCard title="Student Ranking">
      <div className="text-sm text-gray-300">
        Your Rank: <span className="text-yellow-400 font-bold">#42</span>
      </div>
      <div className="h-2 bg-gray-700 rounded mt-2">
        <div className="h-2 bg-yellow-500 rounded w-3/4"></div>
      </div>
    </SectionCard>
  </>
);

const FamilySection = () => (
  <SectionCard title="Family Pass & Group Learning">
    <div className="flex flex-row gap-2">
      <button className="bg-indigo-600 py-2 rounded-lg w-40">
        👨‍👩‍👧 Family Pass
      </button>
      <button className="bg-pink-600 py-2 rounded-lg w-40">
        🎯 Family Quiz
      </button>
    </div>
  </SectionCard>
);

const CuratorSection = () => (
  <>
    <SectionCard title="Curator Dashboard">
      <ul className="list-disc ml-5 [&>li]:text-white text-sm">
        <li>Approve Content</li>
        <li>View Engagement Metrics</li>
        <li>Optimize Galleries</li>
      </ul>
    </SectionCard>

    <SectionCard title="Lean Digital Twin">
      <div className="text-sm text-gray-300">
        Visitor Engagement Index
        <div className="h-2 bg-gray-700 rounded mt-2">
          <div className="h-2 bg-green-500 rounded w-4/5"></div>
        </div>
      </div>
    </SectionCard>
  </>
);

const ContributorSection = () => (
  <SectionCard title="Youth Micro-Boutique">
    <p className="text-sm text-gray-300 mb-3">
      Earn by contributing verified museum knowledge.
    </p>
    <div className="flex flex-row gap-2">
      <button className="bg-emerald-600 py-2 rounded-lg w-40">
        ✍ Create Story
      </button>
      <button className="bg-orange-600 py-2 rounded-lg w-40">
        🌐 Translate
      </button>
    </div>
    <p className="text-sm text-white mt-3">
      💰 Wallet Balance: <span className="text-yellow-400">₹320</span>
    </p>
  </SectionCard>
);

/* ------------------ Main Component ------------------ */

export default function MuseumHome() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [tab, setTab] = useState("visitor");
  
  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 pb-20">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
            <div className="text-lg lg:text-xl font-bold text-yellow-200">
                <FontAwesomeIcon icon={faLandmark} className="text-yellow-500 text-xl lg:text-2xl mr-2" />Bihar Museum
            </div>
            <div className="text-[11px] text-gray-200">Beyond Walls, Digitally Reimagined!</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
                <>
                <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
                <button
                    onClick={handleLogout}
                    className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                    title="Logout"
                >
                    ⏻
                </button>
                </>
            ) : (
                <>
                <button
                    onClick={() => navigate("/login")}
                    className="w-18 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
                >
                    🔐 
                </button>
                </>
            )}
        </div>
        <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
            aria-label="Toggle menu"
            >
            {isMobileMenuOpen ? "×" : "☰"}
        </button>
    </header>

    {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
        {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
            <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
                <div className="flex gap-3">
                <button
                    onClick={handleLogout}
                    className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                >
                    ⏻
                </button>
                </div>
            </div>
        ) : (
            <div className="flex justify-center gap-6">
            <Link to="/login" className="text-teal-300">🔐</Link>
            </div>
        )}
        </div>
    )}

      <div className="min-h-[75vh]">
        <div className="grid grid-cols-5 place-items-center mt-2 mb-2">
            <button
                onClick={() => setTab("visitor")}
                className={`flex flex-col items-center justify-center py-1 text-xs 
                ${tab === "visitor" ? "text-yellow-400" : "text-gray-400 hover:text-gray-200"}`}
                >
                <FontAwesomeIcon icon={faUsers} className="text-lg mb-1" />
                Visitor
            </button>

            <button
                onClick={() => setTab("student")}
                className={`flex flex-col items-center justify-center py-1 text-xs 
                ${tab === "student" ? "text-yellow-400" : "text-gray-400 hover:text-gray-200"}`}
                >
                <FontAwesomeIcon icon={faUserGraduate} className="text-lg mb-1" />
                Student
            </button>

            <button
                onClick={() => setTab("family")}
                className={`flex flex-col items-center justify-center py-1 text-xs 
                ${tab === "family" ? "text-yellow-400" : "text-gray-400 hover:text-gray-200"}`}
                >
                <FontAwesomeIcon icon={faUsers} className="text-lg mb-1" />
                Family
            </button>

            <button
                onClick={() => setTab("curator")}
                className={`flex flex-col items-center justify-center py-1 text-xs 
                ${tab === "curator" ? "text-yellow-400" : "text-gray-400 hover:text-gray-200"}`}
                >
                <FontAwesomeIcon icon={faUserTie} className="text-lg mb-1" />
                Curator
            </button>

            <button
                onClick={() => setTab("contributor")}
                className={`flex flex-col items-center justify-center py-1 text-xs 
                ${tab === "contributor" ? "text-yellow-400" : "text-gray-400 hover:text-gray-200"}`}
                >
                <FontAwesomeIcon icon={faCoins} className="text-lg mb-1" />
                Earn
            </button>
        </div>
        {/* Content */}
        {tab === "visitor" && <VisitorSection />}
        {tab === "student" && <StudentSection />}
        {tab === "family" && <FamilySection />}
        {tab === "curator" && <CuratorSection />}
        {tab === "contributor" && <ContributorSection />}
      </div>      

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/about-us" className="hover:text-teal-300">About</Link>
                <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
        </div>
      </footer>
    </div>
  );
}
